import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { OptionIcon } from "./OptionIcon";

export function MultiSelect({ options, values, onToggle, name }) {
    const selected = new Set(values ?? []);
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {options.map((opt, i) => {
                const isOn = selected.has(opt.value);
                return (
                    <motion.button
                        key={opt.value}
                        type="button"
                        role="checkbox"
                        aria-checked={isOn}
                        onClick={() => onToggle(opt.value)}
                        data-testid={`multi-${name}-${opt.value}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.35,
                            delay: i * 0.04,
                            ease: [0.2, 0.8, 0.2, 1],
                        }}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.985 }}
                        className={`flex items-center gap-4 text-left p-4 rounded-2xl border transition-[background-color,border-color,box-shadow] duration-300 ease-out
                            ${
                                isOn
                                    ? "border-klareet-T bg-klareet-T/10"
                                    : "border-klareet-bor bg-klareet-card hover:border-klareet-T/60 hover:bg-white/[0.03]"
                            }`}
                    >
                        <motion.div
                            className="shrink-0"
                            animate={
                                isOn
                                    ? { rotate: 360, scale: [1, 1.2, 1] }
                                    : { rotate: 0, scale: 1 }
                            }
                            transition={{
                                duration: 0.6,
                                ease: [0.2, 0.8, 0.2, 1],
                            }}
                        >
                            <OptionIcon
                                name={opt.icon}
                                selected={isOn}
                                size={40}
                            />
                        </motion.div>
                        <div className="flex-1 text-[15px] font-medium text-klareet-TEXT tracking-tight">
                            {opt.title}
                        </div>
                        <AnimatePresence mode="wait" initial={false}>
                            {isOn ? (
                                <motion.span
                                    key="on"
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0, opacity: 0 }}
                                    transition={{
                                        type: "spring",
                                        stiffness: 340,
                                        damping: 16,
                                    }}
                                    className="w-6 h-6 rounded-md grid place-items-center border border-klareet-T bg-klareet-T text-klareet-TEXT"
                                >
                                    <Check size={14} strokeWidth={3} />
                                </motion.span>
                            ) : (
                                <motion.span
                                    key="off"
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.9, opacity: 0 }}
                                    transition={{ duration: 0.15 }}
                                    className="w-6 h-6 rounded-md border border-klareet-bor"
                                />
                            )}
                        </AnimatePresence>
                    </motion.button>
                );
            })}
        </div>
    );
}
